import React from 'react';
import styled from '@emotion/styled';
import { Edit2, ChevronRight, Trash2, AlertTriangle, Lightbulb, RefreshCw, Check } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation.jsx';

const Card = styled.div`
  background: ${props => props.$selected ? 'rgba(76, 175, 80, 0.08)' : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid ${props => props.$selected ? 'rgba(76, 175, 80, 0.35)' : 'rgba(255, 255, 255, 0.12)'};
  border-radius: 12px;
  padding: 12px 16px;
  transition: all 0.2s ease;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;

  &:hover {
    background: ${props => props.$selected ? 'rgba(76, 175, 80, 0.12)' : 'rgba(255, 255, 255, 0.08)'};
    border-color: ${props => props.$selected ? 'rgba(76, 175, 80, 0.5)' : 'rgba(255, 255, 255, 0.2)'};
  }
`;

const Checkbox = styled.button`
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: ${props => props.$selected ? '#4CAF50' : 'transparent'};
  border: 2px solid ${props => props.$selected ? '#4CAF50' : 'rgba(255, 255, 255, 0.3)'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s ease;
  padding: 0;
  color: #fff;

  &:hover:not(:disabled) {
    border-color: ${props => props.$selected ? '#66BB6A' : 'rgba(255, 255, 255, 0.5)'};
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

const Title = styled.span`
  font-size: 0.95rem;
  font-weight: ${props => props.$selected ? 600 : 500};
  color: #fff;
  line-height: 1.3;
  flex: 1;
  min-width: 0;
`;

const ActionButton = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: transparent;
  border: 1px solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.25);
  flex-shrink: 0;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.15);
    color: rgba(255, 255, 255, 0.7);
  }
`;

const DeleteButton = styled(ActionButton)`
  &:hover {
    background: rgba(255, 80, 80, 0.15);
    border-color: rgba(255, 80, 80, 0.3);
    color: #ff5555;
  }
`;

const Counts = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;

const CountBadge = styled.span`
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 0.72rem;
  font-weight: 500;
  color: ${p => p.$color};
  opacity: 0.7;
`;

const RefreshButton = styled(ActionButton)`
  width: auto;
  min-width: 28px;
  padding: 0 8px;
  gap: 4px;
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.45);

  &:hover:not(:disabled) {
    color: rgba(255, 255, 255, 0.85);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ViewArrow = styled.div`
  color: rgba(255, 255, 255, 0.2);
  flex-shrink: 0;
  transition: all 0.2s ease;

  ${Card}:hover & {
    color: rgba(255, 255, 255, 0.4);
    transform: translateX(2px);
  }
`;

/**
 * PivotPointCurationCard - Displays a pivot point for Day 2 curation.
 * Click card to select (participant continues via parent onSelect; admin toggles multi-select).
 * `multiSelect` shows a checkbox so the facilitator sees which pivots go forward;
 * without onSelect (locked workshop) the checkbox is read-only.
 */
export default function PivotPointCurationCard({
  pivotPoint,
  edit,
  counts,
  selected,
  multiSelect = false,
  onClick,
  onSelect,
  onEdit,
  onDelete,
  onRefreshClusterAnalysis,
  clusterRefreshPending = false,
}) {
  const { t } = useTranslation();
  const title = edit?.title_override || pivotPoint?.title || t('lenses.untitled');
  const obstacles = counts?.obstacles || 0;
  const opportunities = counts?.opportunities || 0;
  const hasCounts = obstacles > 0 || opportunities > 0;

  const handleCardClick = (e) => {
    if (e.target.closest('[data-action]')) return;
    onSelect?.(pivotPoint);
  };

  return (
    <Card $selected={selected} onClick={handleCardClick}>
      {multiSelect && (
        <Checkbox
          data-action="select"
          type="button"
          role="checkbox"
          aria-checked={!!selected}
          $selected={selected}
          disabled={!onSelect}
          onClick={(e) => { e.stopPropagation(); onSelect?.(pivotPoint); }}
          title={t('lenses.curation.selectTitle')}
        >
          {selected && <Check size={14} strokeWidth={3} />}
        </Checkbox>
      )}
      <Title $selected={selected}>{title}</Title>
      {hasCounts && (
        <Counts>
          {obstacles > 0 && (
            <CountBadge $color="#E94B6E">
              <AlertTriangle size={11} />
              {obstacles}
            </CountBadge>
          )}
          {opportunities > 0 && (
            <CountBadge $color="#A6D64D">
              <Lightbulb size={11} />
              {opportunities}
            </CountBadge>
          )}
        </Counts>
      )}
      {onRefreshClusterAnalysis && (
        <RefreshButton
          data-action="refresh-clusters"
          type="button"
          disabled={clusterRefreshPending}
          onClick={(e) => { e.stopPropagation(); onRefreshClusterAnalysis(pivotPoint); }}
          title={t('lenses.refreshClusterAnalysis')}
        >
          <RefreshCw size={13} style={clusterRefreshPending ? { animation: 'spin 1s linear infinite' } : undefined} />
        </RefreshButton>
      )}
      {onEdit && (
        <ActionButton data-action="edit" onClick={(e) => { e.stopPropagation(); onEdit(pivotPoint); }} title={t('common.edit')}>
          <Edit2 size={14} />
        </ActionButton>
      )}
      {onDelete && (
        <DeleteButton data-action="delete" onClick={(e) => { e.stopPropagation(); onDelete(pivotPoint); }} title={t('common.delete')}>
          <Trash2 size={14} />
        </DeleteButton>
      )}
      {onClick && (
        <ViewArrow>
          <ChevronRight size={18} />
        </ViewArrow>
      )}
    </Card>
  );
}
